import type {
  BindQrPayloadInput,
  QrBindingRecord,
  ResolveQrPayloadResult,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;

interface QrBindingApiModuleDependencies {
  extractApiError: ExtractApiError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeEntityType(value: unknown): "product" | "box" {
  if (value === "product") {
    return "product";
  }
  if (value === "box") {
    return "box";
  }
  throw new Error("Некорректный тип сущности в QR-привязке");
}

function normalizeBinding(value: unknown): QrBindingRecord {
  if (!isRecord(value)) {
    throw new Error("Некорректный формат QR-привязки");
  }

  const payload = String(value.payload || "").trim();
  const entityId = String(value.entityId || "").trim();
  const createdAt = String(value.createdAt || "").trim();
  const updatedAt = String(value.updatedAt || "").trim();
  const entityType = normalizeEntityType(value.entityType);

  if (!payload || !entityId || !createdAt || !updatedAt) {
    throw new Error("Некорректные данные QR-привязки");
  }

  return {
    payload,
    entityId,
    entityType,
    createdAt,
    updatedAt,
  };
}

function normalizeResolveResult(value: unknown): ResolveQrPayloadResult {
  if (!isRecord(value)) {
    throw new Error("Некорректный формат ответа резолва QR-кода");
  }

  const entityId = String(value.entityId || "").trim();
  const entityType = normalizeEntityType(value.entityType);

  if (!entityId) {
    throw new Error("Некорректные данные резолва QR-кода");
  }

  return {
    entityId,
    entityType,
  };
}

function extractConflictEntityId(payload: unknown): string | null {
  if (!isRecord(payload) || !isRecord(payload.details)) {
    return null;
  }

  const entityId = payload.details.entityId;
  if (typeof entityId !== "string") {
    return null;
  }

  const normalized = entityId.trim();
  return normalized || null;
}

async function readResponsePayload(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw.trim()) {
    return {};
  }

  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return {};
  }
}

export class QrBindingConflictError extends Error {
  readonly payload: string;
  readonly requestedEntityId: string;
  readonly existingEntityId: string | null;

  constructor(input: {
    message: string;
    payload: string;
    requestedEntityId: string;
    existingEntityId: string | null;
  }) {
    super(input.message);
    this.name = "QrBindingConflictError";
    this.payload = input.payload;
    this.requestedEntityId = input.requestedEntityId;
    this.existingEntityId = input.existingEntityId;
  }
}

export class QrBindingApiModule {
  constructor(
    private readonly context: Pick<
      WarehouseApiModuleContext,
      "getJson" | "fetchWithAuth"
    >,
    private readonly deps: QrBindingApiModuleDependencies,
  ) {}

  async getQrBindingByEntity(
    entityId: string,
  ): Promise<QrBindingRecord | null> {
    const payload = await this.context.getJson<unknown>(
      `/api/qr-bindings/entity/${encodeURIComponent(entityId)}`,
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError ||
          "Некорректный ответ сервера при загрузке QR-привязки сущности",
      );
    }

    if (payload.data == null) {
      return null;
    }

    return normalizeBinding(payload.data);
  }

  async resolveQrPayload(
    qrPayload: string,
  ): Promise<ResolveQrPayloadResult | null> {
    const payload = await this.context.getJson<unknown>(
      `/api/qr-bindings/resolve/${encodeURIComponent(qrPayload)}`,
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при резолве QR-кода",
      );
    }

    if (payload.data == null) {
      return null;
    }

    return normalizeResolveResult(payload.data);
  }

  async bindQrPayload(input: BindQrPayloadInput): Promise<QrBindingRecord> {
    const normalizedEntityId = input.entityId.trim();
    const normalizedPayload = input.payload.trim();

    const response = await this.context.fetchWithAuth(
      "/api/qr-bindings/bind",
      "POST",
      {
        entityId: normalizedEntityId,
        payload: normalizedPayload,
      },
    );
    const payload = await readResponsePayload(response);

    if (!response.ok) {
      const apiError = this.deps.extractApiError(payload);
      if (response.status === 409) {
        throw new QrBindingConflictError({
          message: apiError || "QR-код уже привязан к другой сущности",
          payload: normalizedPayload,
          requestedEntityId: normalizedEntityId,
          existingEntityId: extractConflictEntityId(payload),
        });
      }

      throw new Error(
        apiError ||
          "Некорректный ответ сервера при сохранении QR-привязки сущности",
      );
    }

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError ||
          "Некорректный ответ сервера при сохранении QR-привязки сущности",
      );
    }

    return normalizeBinding(payload.data);
  }
}
